/**
 * OKLCH: chiarezza percettiva, chroma e tinta.
 *
 * Serve al selettore dei colori personalizzati, che lascia scegliere la SOLA tinta e
 * congela le altre due. Farlo in HSL non funziona: `s` non è una grandezza percettiva,
 * e tenendola costante mentre si gira la ruota la colorfulness reale varia di quasi
 * 4 volte — il giallo esce come un evidenziatore e il blu come un grigino. In OKLCH
 * L e C sono grandezze percettive, quindi congelarle fa davvero quello che promettono:
 * lungo tutta la ruota la chroma resta entro il 10% e la chiarezza entro lo 0.3%.
 *
 * Matrici di Björn Ottosson (https://bottosson.github.io/posts/oklab/), trascritte:
 * il progetto non ha dipendenze di colore e non è il caso di aggiungerne una per
 * trenta righe di aritmetica.
 */

export interface Oklch {
  /** Chiarezza percettiva, 0–1. */
  L: number;
  /** Chroma; 0 è grigio, i colori sRGB arrivano a ~0.37. */
  C: number;
  /** Tinta in gradi, 0–360. */
  h: number;
}

/** sRGB → lineare. Stessa curva di `relativeLuminance`, che è WCAG. */
function toLinear(v: number): number {
  return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
}

function fromLinear(v: number): number {
  return v <= 0.0031308 ? 12.92 * v : 1.055 * v ** (1 / 2.4) - 0.055;
}

function parseHex(hex: string): [number, number, number] {
  const clean = hex.replace('#', '');
  return [
    parseInt(clean.slice(0, 2), 16),
    parseInt(clean.slice(2, 4), 16),
    parseInt(clean.slice(4, 6), 16),
  ];
}

/**
 * Luminanza relativa secondo WCAG.
 *
 * Sta qui e non accanto ai temi dei pezzi perché è una primitiva di colore, e perché
 * le soglie che ne dipendono sono sparse: il contrasto dell'emblema della dama, il
 * pavimento di luminanza della casella scura, il ramo del bordo dell'emblema.
 */
export function relativeLuminance(hex: string): number {
  const [r, g, b] = parseHex(hex).map((v) => toLinear(v / 255));
  return 0.2126 * r! + 0.7152 * g! + 0.0722 * b!;
}

/** Coordinate CARTESIANE di OKLab: le due opposizioni cromatiche, non tinta e chroma. */
export interface Oklab {
  L: number;
  /** Verde ↔ rosso. */
  a: number;
  /** Blu ↔ giallo. */
  b: number;
}

/**
 * OKLab cartesiano.
 *
 * È la forma giusta per misurare una DISTANZA fra due colori: `Math.hypot` sulle tre
 * coordinate è la differenza percettiva. Sottrarre due tinte in gradi, invece, darebbe
 * numeri enormi fra colori quasi grigi, dove la tinta è solo rumore d'arrotondamento.
 * `hexToOklch` è la stessa trasformazione in coordinate polari, comoda per COSTRUIRE un
 * colore invece che per confrontarne due.
 */
export function oklab(hex: string): Oklab {
  const [R, G, B] = parseHex(hex).map((v) => toLinear(v / 255)) as [number, number, number];

  const l = Math.cbrt(0.4122214708 * R + 0.5363325363 * G + 0.0514459929 * B);
  const m = Math.cbrt(0.2119034982 * R + 0.6806995451 * G + 0.1073969566 * B);
  const s = Math.cbrt(0.0883024619 * R + 0.2817188376 * G + 0.6299787005 * B);

  return {
    L: 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    a: 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    b: 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  };
}

export function hexToOklch(hex: string): Oklch {
  const { L, a, b } = oklab(hex);
  return { L, C: Math.hypot(a, b), h: ((Math.atan2(b, a) * 180) / Math.PI + 360) % 360 };
}

/** Componenti sRGB in 0–255, ancora in virgola mobile e non ancora limitate. */
function oklabToRgb(L: number, a: number, b: number): [number, number, number] {
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;

  return [
    fromLinear(4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s) * 255,
    fromLinear(-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s) * 255,
    fromLinear(-0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s) * 255,
  ];
}

/** Quanto un colore esce dal cubo sRGB, in livelli. 0 = ci sta dentro. */
function gamutExcess([r, g, b]: [number, number, number]): number {
  return Math.max(0, -r, r - 255, -g, g - 255, -b, b - 255);
}

/**
 * Passo con cui si riduce la chroma quando il colore esce dal gamut sRGB.
 *
 * Ridurre la chroma — e non limitare i canali — è ciò che conserva tinta e chiarezza:
 * limitare i canali sposta entrambe, ed è il motivo per cui un blu saturo "clippato"
 * diventa violetto. Con i profili in uso serve solo alla casella chiara e per una
 * quarantina di tinte su 360, dove toglie il 7% di chroma: invisibile.
 */
const CHROMA_STEP = 0.0005;

export function oklchToHex(L: number, C: number, hue: number): string {
  const rad = ((hue % 360) * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);

  let c = C;
  let rgb = oklabToRgb(L, c * cos, c * sin);
  // Mezzo livello di tolleranza: sotto, l'arrotondamento a otto bit riporta comunque
  // il canale dentro, e togliere chroma per quello sarebbe zelo senza effetto.
  while (gamutExcess(rgb) > 0.5 && c > 0) {
    c = Math.max(0, c - CHROMA_STEP);
    rgb = oklabToRgb(L, c * cos, c * sin);
  }

  const byte = (v: number) =>
    Math.min(255, Math.max(0, Math.round(v)))
      .toString(16)
      .padStart(2, '0');
  return `#${byte(rgb[0])}${byte(rgb[1])}${byte(rgb[2])}`;
}

/**
 * Quanto due colori si distinguono, come distanza euclidea in OKLab.
 *
 * È la misura giusta ovunque serva rispondere a «questi due si vedono come diversi?»,
 * e in particolare è quella che NON va sostituita con il contrasto WCAG. Il contrasto
 * guarda la sola luminanza: due colori separati dalla tinta lo sfondano pur essendo
 * perfettamente distinguibili. Fra le palette spedite, `blue-red` ha contrasto 1.11 e
 * `orange-blue` 2.13 — numeri da «illeggibile» — mentre in ΔE stanno a 0.318 e 0.349,
 * cioè larghissimi. Una di quelle due è la coppia pensata apposta per il daltonismo.
 */
export function perceptualDistance(a: string, b: string): number {
  const p = oklab(a);
  const q = oklab(b);
  return Math.hypot(p.L - q.L, p.a - q.a, p.b - q.b);
}

/**
 * Sotto questa distanza due colori dello stesso asse — le due pedine, le due caselle —
 * non si distinguono abbastanza da giocarci.
 *
 * Calibrata sui valori reali, con un ordine di grandezza di margine da entrambi i lati:
 * la peggiore coppia mai spedita sta a 0.231 (`sand`, ritirata), la peggiore fra quelle
 * offerte oggi a 0.236 (`wood-classic`, che il test salta perché dichiarata coi token CSS)
 * e a 0.249 fra quelle che il test misura (`marble`), mentre due colori davvero
 * indistinguibili stanno sotto 0.08 (due crema vicini: 0.076; due grigi vicini: 0.034;
 * identici: 0).
 *
 * Le voci di variante entrate negli elenchi non spostano il fondo: la più stretta è la
 * scacchiera dell'Italiana a 0.273, in mezzo al gruppo.
 *
 * Serve solo ad AVVISARE. La scelta dei colori è dell'utente, e se vuole due marroni
 * quasi uguali il gioco continua a funzionare: le evidenziazioni della scacchiera hanno
 * una garanzia loro, che non dipende da questa.
 */
export const MIN_PAIR_DISTANCE = 0.15;

/**
 * Banda morta della soglia: l'avviso si accende sotto `MIN_PAIR_DISTANCE` e si spegne solo
 * sopra `MIN_PAIR_DISTANCE + PAIR_DISTANCE_HYSTERESIS`.
 *
 * Senza banda morta, trascinando nell'area del selettore si attraversa la soglia decine di
 * volte al secondo e l'avviso lampeggia. Non è un dettaglio estetico: un avviso che sfarfalla
 * si legge come un difetto, non come un consiglio.
 *
 * 0.02 sta dalla parte sicura per lo stesso motivo per cui la soglia sta a 0.15: la peggiore
 * coppia mai spedita è a 0.231, quindi anche il bordo alto della banda (0.17) resta lontano
 * dalle palette vere.
 */
export const PAIR_DISTANCE_HYSTERESIS = 0.02;
